"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Banknote,
  Bot,
  Code2,
  Globe2,
  GraduationCap,
  Layers,
  MapPin,
  MessageCircleMore,
  Plug,
  Scale,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import {
  CLAUDE_PARTNER_APPROVED_LINE,
  ClaudePartnerCertifiedBadge,
} from "@/components/ui/anthropic-partner-badge";
import ServiceFaq from "@/components/services/ServiceFaq";
import { StructuredData } from "@/components/common";
import { anthropicPartnerFaqs } from "@/content/anthropic-partner-faqs";
import { anthropicPartnerKeyFacts } from "@/content/anthropic-partner-key-facts";
import { renderAccent } from "@/lib/render-accent";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { whatsappHref } from "@/lib/whatsapp";
import GovernanceCallout from "@/components/ai-governance/GovernanceCallout";

/** What a buyer in Malaysia is actually shopping for when they search "Claude
 *  partner Malaysia": a local company they can hire, not an explainer about
 *  Anthropic. Every claim here is one we hold and can evidence. */
const localDelivery: Array<{ title: string; description: string; icon: LucideIcon }> = [
  {
    title: "We deploy Claude, we don't resell it",
    description:
      "Anthropic sells Claude directly, and card buyers go straight to them. What a partner adds is the build — agent architecture, model selection, evaluation, and the production system that keeps running after launch. If your company must pay by purchase order, we can also help you buy Claude in RM or SGD.",
    icon: Wrench,
  },
  {
    title: "Delivered from Selangor",
    description:
      "Our engineering team works out of Setia Alam, Shah Alam, and on site with teams across the Klang Valley when a deployment calls for it. Same time zone, same working week.",
    icon: MapPin,
  },
  {
    title: "HRD Corp claimable training",
    description:
      "We are an HRD Corp approved training provider, so enabling your team on Claude can be claimed against your levy instead of coming out of the project budget.",
    icon: GraduationCap,
  },
  {
    title: "MDEC Malaysia Digital status",
    description:
      "Anchor Sprint holds Malaysia Digital status, certified by MDEC — the status your procurement team checks when it wants to know whether a vendor is real.",
    icon: BadgeCheck,
  },
  {
    title: "Scoped and invoiced in MYR",
    description:
      "Quotes, support contracts and run-cost estimates in ringgit, against Malaysian business hours — not a USD price list you have to translate for finance.",
    icon: Banknote,
  },
  {
    title: "Singapore too, when you need both",
    description:
      "Many of our clients run entities on both sides of the causeway. We deliver the same standard in Singapore, scoped to PDPC and MAS expectations where they apply.",
    icon: Globe2,
  },
];

/* The capability roll-call, deliberately a flat <ul> of short clauses.
 *
 *  Google's AI Overview described us as "custom Claude-powered enterprise
 *  workflows, MYR billing, and localized agent deployment out of Selangor" while
 *  giving Agmo "MCP integrations, RAG pipelines, and HRD Corp-claimable training
 *  led by certified architects". The gap was not ranking (we hold #1) and not
 *  citation count (Google cites us 3 of 5) — it was that our capabilities lived
 *  in prose cards, with no extractable list anywhere on the page. Answer engines
 *  lift short, self-contained clauses; keep each item under ~15 words and lead
 *  with the capability noun. */
const capabilities: Array<{ name: string; clause: string }> = [
  {
    name: "Claude managed service",
    clause: "we run the agents in production, monitor drift, and stay on call after launch.",
  },
  {
    name: "Custom agent deployment",
    clause: "agents built on the Claude Agent SDK for your workflows, not off-the-shelf bots.",
  },
  {
    name: "MCP development and integration",
    clause: "Model Context Protocol connectors into SQL Account, AutoCount, Odoo, NetSuite and Salesforce.",
  },
  {
    name: "RAG pipelines and custom agents",
    clause: "retrieval-augmented generation over your own documents, with the retrieval evaluated before launch.",
  },
  {
    name: "On-demand FDE teams",
    clause: "forward-deployed engineers embed with your team to ship Claude agents, scaled as needed.",
  },
  {
    name: "Authorised GPU hardware",
    clause: "authorised reseller of NVIDIA DGX Spark, Dell and Lenovo GPU workstations and servers, quoted in MYR, for AI that runs on your own premises.",
  },
  {
    name: "HRD Corp claimable Claude training",
    clause: "an approved HRD Corp provider, so team enablement runs against your levy.",
  },
  {
    name: "A full team of 10 Claude Certified Architects (CCAR-F)",
    clause: "the architect who designs your system signs off its evaluation.",
  },
  {
    name: "Malaysia and Singapore delivery",
    clause: "scoped and invoiced in MYR or SGD, under PDPA or PDPC and MAS as they apply.",
  },
];

/* Answer-first entity block (2026-09-30). Sits straight under the hero so the
 *  first thing after the H1 is uninterrupted prose that answers "who is a Claude
 *  partner in Malaysia", then a flat definition list an answer engine can lift
 *  field by field. Previously the hero was followed by a stack of promo links,
 *  so AI Overview/AI Mode described us from /our-partners/ instead. Plain-text
 *  partner line only (no logo); never "official partner" or "we certify": the
 *  certification is awarded by Anthropic. See
 *  docs/analytics/competitors/2026-09-30-claude-partner-malaysia.md, section 5.3. */
const answerFacts: Array<{ term: string; detail: string }> = [
  {
    term: "Services",
    detail:
      "Claude managed service, consulting and implementation, RAG pipelines and custom agents built with the Claude Agent SDK, Claude Code and MCP, and on-demand FDE teams.",
  },
  {
    term: "Credentials",
    detail:
      "Malaysian homegrown (Malaysian-owned). Certified in the Claude Partner Network. A full team of 10 Claude Certified Architects (CCAR-F). MDEC Malaysia Digital status. HRD Corp Approved Training Provider.",
  },
  {
    term: "Locations",
    detail: "Setia Alam, Shah Alam, Selangor. Suntec, Singapore.",
  },
  {
    term: "Billing",
    detail: "MYR, or SGD in Singapore.",
  },
  {
    term: "Training",
    detail: "Official Claude certification prep, exam included. HRD Corp claimable.",
  },
];

const offerings: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: "Claude Developer Platform",
    description:
      "The API behind Claude: tool use, structured outputs, prompt caching and batch processing — available directly from Anthropic and through major cloud platforms.",
    icon: Layers,
  },
  {
    title: "An agent platform, not just a chatbot",
    description:
      "The Claude Agent SDK, Managed Agents, and Claude Code let teams build autonomous agents that plan, call tools, and complete real multi-step work.",
    icon: Bot,
  },
  {
    title: "Model Context Protocol (MCP)",
    description:
      "An open standard, created by Anthropic, for connecting AI agents to the tools and data they need — now adopted across the industry.",
    icon: Plug,
  },
];

// Capability depth vs speed are relative within the family — visual guide,
// not a benchmark claim.
const models: Array<{
  name: string;
  tagline: string;
  depth: number;
  speed: number;
}> = [
  {
    name: "Claude Fable 5",
    tagline: "The frontier — deepest reasoning, longest-horizon agents",
    depth: 100,
    speed: 40,
  },
  {
    name: "Claude Opus",
    tagline: "The workhorse for complex agentic and knowledge work",
    depth: 88,
    speed: 55,
  },
  {
    name: "Claude Sonnet",
    tagline: "The production balance of speed and intelligence",
    depth: 70,
    speed: 80,
  },
  {
    name: "Claude Haiku",
    tagline: "Fast and cost-efficient at high volume",
    depth: 50,
    speed: 100,
  },
];

const flowSteps: Array<{ label: string; sublabel: string }> = [
  { label: "Your request", sublabel: "a goal, not a script" },
  { label: "Claude agent", sublabel: "plans · calls tools · verifies" },
  { label: "Your systems", sublabel: "CRM · ERP · documents, via MCP" },
  { label: "Work completed", sublabel: "with an auditable trail" },
];

const usage: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: "Agentic platforms built on Claude",
    description:
      "We design and ship autonomous agents using Claude's tool use, the Claude Agent SDK, and MCP — agents that handle workflows end to end, not chatbots that stop at answering.",
    icon: Bot,
  },
  {
    title: "The right Claude model for each workload",
    description:
      "We match models to jobs: Opus-tier reasoning for complex agent loops and analysis, Sonnet for production assistants, Haiku for high-volume classification and routing — so you pay for exactly the capability you need.",
    icon: Scale,
  },
  {
    title: "Claude Code in our own engineering",
    description:
      "Our team builds with Claude Code every day — including this website. We bring that first-hand agentic engineering experience to every client project.",
    icon: Code2,
  },
  {
    title: "Honest architecture guidance",
    description:
      "Claude via API where frontier capability matters; private open-weight LLMs on your infrastructure where data must never leave. We help you choose the right mix — and build both.",
    icon: ShieldCheck,
  },
];

interface AnthropicPartnerProps {
  locale?: Locale;
}

export default function AnthropicPartner({
  locale = defaultLocale,
}: AnthropicPartnerProps) {
  const prefersReducedMotion = useReducedMotion();

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.55, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="bg-background pb-16 pt-20 lg:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div>
              <motion.p
                {...reveal(0)}
                className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
              >
                {t(locale, "Anthropic Claude Partner · Malaysia")}
              </motion.p>
              <motion.h1
                {...reveal(0.08)}
                className="font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl"
              >
                {/* 2026-10-03: "Certified" added with the <title> (they move together).
                    Entity claim, mirroring /singapore/claude-partner/ ("A Claude
                    partner in Singapore"), which holds the same position at ~3x
                    the CTR. Google was rewriting the <title> from this H1, so the
                    two have to move together. "& Singapore" is dropped on purpose:
                    the sub-paragraph and the link below still carry the SG signal,
                    and Singapore has its own page that already outranks this one
                    there — splitting the H1 only blunted the Malaysia term. The
                    verification material stays on the page, one section down. */}
                {renderAccent(t(locale, "[[Malaysia's]] Homegrown Anthropic Claude Partner"))}
              </motion.h1>
              <motion.p
                {...reveal(0.16)}
                className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted md:text-xl"
              >
                {t(
                  locale,
                  "Anchor Sprint is Malaysia's homegrown Claude partner, Certified in the Claude Partner Network, and an AI agency focused entirely on Claude. A full team of 10 Claude Certified Architects (CCAR-F) designs, deploys, and supports Claude-powered agents for enterprises across Malaysia, Singapore and ASEAN.",
                )}
              </motion.p>
              <motion.p {...reveal(0.2)} className="mt-3 text-sm">
                {/* SG section is English-only — plain href (locale prefix would 404) */}
                <a
                  href="/singapore/claude-partner/"
                  className="font-semibold text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
                >
                  {t(locale, "Also a Claude partner in Singapore →")}
                </a>
              </motion.p>
              <motion.div
                {...reveal(0.24)}
                className="mt-8 flex flex-wrap items-center gap-4"
              >
                <a
                  href={whatsappHref("Hi Anchor Sprint, I'd like to talk about Claude for my company (from your Claude Partner page).")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track-event="contact_whatsapp_click"
                  data-track-location="anthropic_partner_hero"
                  data-track-phone="601159246128"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
                >
                  <FaWhatsapp className="h-4 w-4" aria-hidden />
                  {t(locale, "WhatsApp us about Claude")}
                </a>
                <Link
                  href={getLocalizedPathname("/contact/", locale)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-7 py-3 text-sm font-semibold text-neutral-emphasis transition hover:border-primary hover:text-primary"
                >
                  <MessageCircleMore className="h-4 w-4" aria-hidden />
                  {t(locale, "Talk to us about Claude")}
                </Link>
              </motion.div>
              {/* The page's one official partner badge, with the approved line
                  (English on every locale). Own row, away from other logos. */}
              <motion.div {...reveal(0.28)} className="mt-10">
                <ClaudePartnerCertifiedBadge />
              </motion.div>
            </div>

            {/* Editorial artwork — framed media */}
            <motion.div
              {...reveal(0.2)}
              className="overflow-hidden rounded-2xl border border-border"
            >
              <img
                src="/images/partners/anthropic-editorial.jpg"
                alt={t(
                  locale,
                  "Abstract editorial artwork of flowing ink lines on warm paper",
                )}
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
        <div className="mt-16 h-px w-full bg-border" aria-hidden />
      </section>

      {/* Answer-first block: see the `answerFacts` comment. Keep it above the
          promo links below, which used to interrupt the answer. */}
      <section className="bg-background pb-16 pt-4 lg:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
            <div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {t(locale, "The short answer")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
                {t(locale, "Who is a Claude partner in Malaysia?")}
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "Anchor Sprint is a Claude partner in Malaysia: a Malaysian homegrown firm Certified in the Claude Partner Network, based in Setia Alam, Selangor, with a Singapore office at Suntec. A full team of 10 Claude Certified Architects (CCAR-F) designs, builds and runs Claude agents in production, from RAG pipelines and custom agents to on-demand FDE teams, and prepares teams for the official Claude certification through HRD Corp claimable training. We quote in MYR, and in SGD in Singapore.",
                )}
              </p>
            </div>
            <dl className="border-t border-border">
              {answerFacts.map(({ term, detail }) => (
                <div
                  key={term}
                  className="grid gap-1 border-b border-border py-4 sm:grid-cols-[8.5rem_1fr] sm:gap-6"
                >
                  <dt className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-neutral-muted sm:pt-1">
                    {t(locale, term)}
                  </dt>
                  <dd className="text-base leading-relaxed text-neutral-emphasis">
                    {t(locale, detail)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Key facts block (2026-10-08): see src/content/anthropic-partner-key-facts.ts.
          One plain, self-contained sentence per fact, so an AI answer can lift
          any line on its own. The approved Anthropic line leads, verbatim and in
          English on every locale (data-i18n-skip keeps the DOM translator off it). */}
      <section
        aria-labelledby="anchor-sprint-at-a-glance"
        className="bg-background pb-16 lg:pb-20"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 border-t border-border pt-10 lg:grid-cols-[1fr_2.1fr] lg:gap-16">
            <div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {t(locale, "Key facts")}
              </p>
              <h2
                id="anchor-sprint-at-a-glance"
                className="font-display text-2xl font-bold leading-tight text-neutral-emphasis md:text-3xl"
              >
                {t(locale, "Anchor Sprint at a glance")}
              </h2>
              <p className="mt-3 text-sm text-neutral-muted">
                {t(locale, "Facts as of October 2026.")}
              </p>
            </div>
            <ul className="grid gap-x-10 sm:grid-cols-2">
              <li
                data-i18n-skip
                className="flex gap-3 border-b border-border py-4 text-base leading-relaxed text-neutral-emphasis"
              >
                <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                <span>{CLAUDE_PARTNER_APPROVED_LINE}</span>
              </li>
              {anthropicPartnerKeyFacts.map((fact) => (
                <li
                  key={fact}
                  className="flex gap-3 border-b border-border py-4 text-base leading-relaxed text-neutral-emphasis"
                >
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                  <span>{t(locale, fact)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Hook: promote the Claude Cowork & plans guides */}
      <section className="bg-neutral-surface">
        <div className="mx-auto max-w-7xl border-t border-border px-4 sm:px-6 lg:px-8">
          <Link
            href="/anthropic-claude-partner-malaysia-singapore/"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">
                {t(locale, "Why do we build on Claude?")}
              </span>{" "}
              <span className="text-neutral-muted">
                {t(
                  locale,
                  "The engineering case for Claude over OpenAI, where we recommend against it, and the team that deploys it in Malaysia and Singapore.",
                )}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Read why we chose Claude")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
          <Link
            href="/guides/claude-cowork/"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">
                {t(locale, "New to Claude Cowork?")}
              </span>{" "}
              <span className="text-neutral-muted">
                {t(
                  locale,
                  "Our complete guide — what it is, how to use it, and where a custom agent takes over.",
                )}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Read the Cowork guide")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
          <Link
            href="/guides/claude-plans-malaysia/"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">
                {t(locale, "Looking for a Claude plan guide?")}
              </span>{" "}
              <span className="text-neutral-muted">
                {t(
                  locale,
                  "Check out our latest plan & pricing research — every tier compared for Malaysian teams.",
                )}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Compare Claude plans")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
          <Link
            href="/guides/how-to-use-claude-malaysia/"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">
                {t(locale, "New to Claude in Malaysia?")}
              </span>{" "}
              <span className="text-neutral-muted">
                {t(
                  locale,
                  "Our complete guide to accessing and using Claude in Malaysia — sign-up, payment, and business access.",
                )}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Using Claude in Malaysia")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
          {/* This page sells "HRD Corp claimable training" in its own copy but
              never linked the page that ranks for those queries — which sat on
              page 2 (p13.4) with only footer links pointing at it. */}
          <Link
            href="/services/training-education/"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">
                {t(locale, "Need your team trained, not just the software installed?")}
              </span>{" "}
              <span className="text-neutral-muted">
                {t(
                  locale,
                  "We are an HRD Corp approved provider — AI Fluency courses taught on site, claimable under SBL Khas against the levy you already pay.",
                )}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "See HRD Corp claimable training")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
          <Link
            href="/solutions/agent-use-cases/"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">
                {t(locale, "Want to see agents we've built?")}
              </span>{" "}
              <span className="text-neutral-muted">
                {t(
                  locale,
                  "Browse our agent use case directory — credit evaluation, order processing, claims and more, with how we build each one.",
                )}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Browse agent use cases")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
          <Link
            href="/tools/claude-cost-calculator/"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">
                {t(locale, "Budgeting Claude for your team?")}
              </span>{" "}
              <span className="text-neutral-muted">
                {t(
                  locale,
                  "Compare Claude Team vs Enterprise over 3 years and download an Excel TCO for management.",
                )}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Open the cost calculator")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
        </div>
      </section>

      {/* What a Claude partner in Malaysia does — the local-intent block. This
          page ranked for "claude partner" but nowhere for "claude partner
          malaysia": every heading below the H1 was about Anthropic, so Google
          read it as an explainer rather than a vendor you can hire here. */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Working with us in Malaysia")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "What partner status proves — and what it does not")}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "A partner badge on its own proves very little, and we will say that about ourselves before we say it about anyone else. What is worth checking is narrower: who holds the proctored certifications, whether those people will work on your project, and what the firm has actually put into production. Here is how to check all three, including on us.",
              )}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {localDelivery.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7"
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {t(locale, description)}
                </p>
              </motion.div>
            ))}
          </div>

          {/* The extractable capability list — see the `capabilities` comment. */}
          <motion.div {...inView} className="mt-16 max-w-3xl">
            <h3 className="font-display text-2xl font-bold leading-tight text-neutral-emphasis md:text-3xl">
              {t(locale, "What Anchor Sprint delivers as a Claude partner")}
            </h3>
            <ul className="mt-6 space-y-3">
              {capabilities.map(({ name, clause }) => (
                <li key={name} className="flex gap-3 text-base leading-relaxed text-neutral-muted">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                  <span>
                    <strong className="font-semibold text-neutral-emphasis">
                      {t(locale, name)}
                    </strong>
                    {" — "}
                    {t(locale, clause)}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Editorial links to the aideploy inner pages. The footer already links
              aideploy, but a sitewide footer link is worth roughly one weak signal
              however many pages repeat it — which is why every aideploy inner page
              still reads "Discovered - currently not indexed" with lastCrawlTime
              null. These are in-content links from a page Google crawls often. */}
          {/* Sends Singapore intent to the Singapore page. Google was serving this
              Malaysian page for "claude partner singapore" — a searcher there landed
              on a result headed "Claude Partner Malaysia". */}
          <motion.p {...inView} className="mt-10 max-w-3xl leading-relaxed text-neutral-muted">
            {t(locale, "Looking for Singapore? Anchor Sprint Pte. Ltd. delivers from Suntec Tower One — see ")}
            <Link
              href={getLocalizedPathname("/singapore/claude-partner/", locale)}
              className="font-medium text-primary underline underline-offset-4"
            >
              {t(locale, "our Claude partner page for Singapore")}
            </Link>
            {t(locale, ", scoped in SGD with PDPC-aligned governance.")}
          </motion.p>

          <motion.p {...inView} className="mt-6 max-w-3xl leading-relaxed text-neutral-muted">
            {t(locale, "Deployment work runs through our delivery site: ")}
            <a
              href="https://aideploy.my/services/"
              className="font-medium text-primary underline underline-offset-4"
            >
              {t(locale, "what an AI agent deployment in Malaysia involves")}
            </a>
            {t(locale, ", and ")}
            <a
              href="https://aideploy.my/pricing/"
              className="font-medium text-primary underline underline-offset-4"
            >
              {t(locale, "what it costs")}
            </a>
            {t(
              locale,
              ". For the worked examples — order processing, invoice matching, knowledge retrieval — see ",
            )}
            <a
              href="https://aideploy.my/use-cases/"
              className="font-medium text-primary underline underline-offset-4"
            >
              {t(locale, "the Malaysian use-case library")}
            </a>
            {t(locale, ".")}
          </motion.p>

          <motion.p {...inView} className="mt-8 max-w-3xl text-sm text-neutral-muted/80">
            {t(
              locale,
              "Anchor Sprint is Certified in the Claude Partner Network. We are not an exclusive, official or endorsed representative of Anthropic in Malaysia, and no such arrangement exists for anyone here.",
            )}
          </motion.p>
        </div>
      </section>

      {/* Who is Anthropic */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <motion.div {...inView}>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {t(locale, "Who is Anthropic")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
                {t(locale, "The")}{" "}
                <span className="emphasis-underline">
                  {t(locale, "AI safety company")}
                </span>{" "}
                {t(locale, "behind Claude")}
              </h2>
            </motion.div>
            <motion.div {...inView} className="space-y-5 text-lg leading-relaxed text-neutral-muted">
              <p>
                {t(
                  locale,
                  "Anthropic is an AI safety and research company headquartered in San Francisco, founded in 2021. It builds Claude — one of the world's most capable AI model families — with safety research at the core of how the models are trained and deployed.",
                )}
              </p>
              <p>
                {t(
                  locale,
                  "Claude is trusted by enterprises worldwide for reasoning, coding, and agentic work, and Anthropic does not use business customers' API data to train its models by default.",
                )}
              </p>
              <p>
                {t(
                  locale,
                  "In 2026 Anthropic launched the Claude Partner Network, connecting businesses with partners that build and deploy Claude solutions. Anchor Sprint is Certified in the Claude Partner Network.",
                )}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Claude model family — visual spectrum */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "The models")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "One family,")}{" "}
              <span className="emphasis-underline">
                {t(locale, "every workload")}
              </span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {models.map(({ name, tagline, depth, speed }, index) => (
              <motion.div
                key={name}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7"
              >
                <h3 className="font-display text-xl font-bold text-neutral-emphasis">
                  {name}
                </h3>
                <p className="mb-6 mt-2 flex-1 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, tagline)}
                </p>
                <div className="space-y-3">
                  <div>
                    <div className="mb-1.5 flex items-center justify-between font-mono text-xs uppercase tracking-wider text-neutral-muted/70">
                      <span>{t(locale, "Reasoning depth")}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-neutral-subtle">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${depth}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1.5 flex items-center justify-between font-mono text-xs uppercase tracking-wider text-neutral-muted/70">
                      <span>{t(locale, "Speed & cost efficiency")}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-neutral-subtle">
                      <div
                        className="h-full rounded-full bg-neutral-emphasis/70"
                        style={{ width: `${speed}%` }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.p
            {...inView}
            className="mt-8 max-w-2xl text-sm text-neutral-muted/80"
          >
            {t(
              locale,
              "Relative comparison within the Claude family — we benchmark your actual workload before recommending a model.",
            )}
          </motion.p>
        </div>
      </section>

      {/* What Anthropic offers */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "The platform")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "What Anthropic offers")}
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {offerings.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {t(locale, description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we use Anthropic */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "The partnership in practice")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "How we")}{" "}
              <span className="emphasis-underline">{t(locale, "build")}</span>{" "}
              {t(locale, "with Claude for Malaysian businesses")}
            </h2>
          </motion.div>

          {/* Agent flow — dark proof moment */}
          <motion.div
            {...inView}
            className="mb-12 rounded-3xl bg-hero-background p-8 sm:p-10"
          >
            <p className="mb-8 font-mono text-xs uppercase tracking-[0.25em] text-primary-contrast/50">
              {t(locale, "How a Claude agent moves work through your business")}
            </p>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-0">
              {flowSteps.map(({ label, sublabel }, index) => (
                <div
                  key={label}
                  className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-center"
                >
                  <div
                    className={
                      index === 1
                        ? "flex-1 rounded-2xl border border-primary/50 bg-primary/10 p-5"
                        : "flex-1 rounded-2xl border border-primary-contrast/15 p-5"
                    }
                  >
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono text-xs text-primary">
                        0{index + 1}
                      </span>
                      <p className="font-display text-base font-semibold text-primary-contrast">
                        {t(locale, label)}
                      </p>
                    </div>
                    <p className="mt-1.5 pl-7 font-mono text-xs text-primary-contrast/60">
                      {t(locale, sublabel)}
                    </p>
                  </div>
                  {index < flowSteps.length - 1 && (
                    <ArrowRight
                      className="mx-auto h-5 w-5 shrink-0 rotate-90 text-primary/70 lg:mx-3 lg:rotate-0"
                      aria-hidden
                    />
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {usage.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: index * 0.06,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="flex gap-5 rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div>
                  <div className="mb-2 flex items-baseline gap-3">
                    <span className="font-mono text-sm text-neutral-muted/60">
                      0{index + 1}
                    </span>
                    <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                      {t(locale, title)}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-neutral-muted">
                    {t(locale, description)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          {/* Built with Claude — real, linkable proof */}
          <motion.div {...inView} className="mt-16">
            <p className="mb-6 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Built with Claude")}
            </p>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Link
                href={getLocalizedPathname("/solutions/agent-use-cases/", locale)}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-neutral-border"
              >
                <h3 className="mb-2 font-display text-base font-semibold text-neutral-emphasis">
                  {t(locale, "Custom agents for clients")}
                </h3>
                <p className="mb-5 flex-1 text-sm leading-relaxed text-neutral-muted">
                  {t(
                    locale,
                    "Client agents are custom builds on Claude, designed around each workflow and tested before go-live. See how we approach common workflows.",
                  )}
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 group-hover:gap-2.5">
                  {t(locale, "Browse agent use cases")}
                  <ArrowUpRight className="h-4 w-4" aria-hidden />
                </span>
              </Link>
              <Link
                href={getLocalizedPathname(
                  "/blog/claude-opus-4-8-malaysian-businesses/",
                  locale,
                )}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-neutral-border"
              >
                <h3 className="mb-2 font-display text-base font-semibold text-neutral-emphasis">
                  {t(locale, "From our blog")}
                </h3>
                <p className="mb-5 flex-1 text-sm leading-relaxed text-neutral-muted">
                  {t(
                    locale,
                    "Our take on what the latest Claude models mean for Malaysian businesses, from the team deploying them.",
                  )}
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 group-hover:gap-2.5">
                  {t(locale, "Read the article")}
                  <ArrowUpRight className="h-4 w-4" aria-hidden />
                </span>
              </Link>
            </div>
          </motion.div>

          <motion.p {...inView} className="mt-10 max-w-3xl text-sm text-neutral-muted">
            {CLAUDE_PARTNER_APPROVED_LINE}
          </motion.p>
          <motion.p {...inView} className="mt-3 max-w-3xl text-sm text-neutral-muted">
            {t(
              locale,
              "Our team includes 10 Claude Certified Architects — the certification for designing and building production systems on Claude.",
            )}
          </motion.p>
        </div>
      </section>

      <section className="bg-background px-4 py-12 sm:px-6 lg:px-8">
        <GovernanceCallout locale={locale} variant="rollout" placement="anthropic_partner" />
      </section>

      {/* FAQ — matches the FAQPage structured data */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "FAQ")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Common questions about working with a")}{" "}
              <span className="emphasis-underline">
                {t(locale, "Claude partner in Malaysia")}
              </span>
            </h2>
          </motion.div>
          <StructuredData type="faq" faqs={anthropicPartnerFaqs} />
          <ServiceFaq items={anthropicPartnerFaqs} />
        </div>
      </section>

      {/* CTA — dark proof moment */}
      <section className="bg-background pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...inView}
            className="rounded-3xl bg-hero-background p-10 sm:p-14"
          >
            <div className="max-w-3xl">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
                {t(locale, "Start a conversation")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
                {t(locale, "Ready to work with a Claude partner in Malaysia?")}
              </h2>
              <p className="mt-4 text-lg text-primary-contrast/70">
                {t(
                  locale,
                  "From a first pilot agent to a production deployment — tell us what you're trying to automate and we'll map the fastest path to a working system.",
                )}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={whatsappHref("Hi Anchor Sprint, I'd like to talk about Claude for my company (from your Claude Partner page).")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track-event="contact_whatsapp_click"
                  data-track-location="anthropic_partner_cta"
                  data-track-phone="601159246128"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
                >
                  <FaWhatsapp className="h-4 w-4" aria-hidden />
                  {t(locale, "WhatsApp us about Claude")}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
